/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logQnaKBButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerDiscardModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerDiscardModal';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerSynonymGroupRightCommandBarProps = {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A boolean flag indicating if there is no entries.
     */
    isInEmptyState: boolean;

    /**
     * A callback on save changes click.
     */
    onSave: () => void;

    /**
     * A callback on discard changes click.
     */
    onDiscard: () => void;

    /**
     * A boolean flag indicating if synonym group is being edited.
     */
    isEditSynonymOn: boolean;

    /**
     * A callback to edit synonym.
     */
    onEdit: () => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable edit actions.
     */
    areEditActionsDisabled: boolean;
};

const messages = defineMessages({
    saveChanges: {
        id: 'qnaMaker.qnaMakerSynonymGroupRightCommandBar.saveChanges',
        defaultMessage: 'Save changes'
    },
    edit: {
        id: 'qnaMaker.qnaMakerSynonymGroupRightCommandBar.edit',
        defaultMessage: 'Edit'
    },
    discardChanges: {
        id: 'qnaMaker.qnaMakerSynonymGroupRightCommandBar.discardChanges',
        defaultMessage: 'Discard changes'
    }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerSynonymGroupRightCommandBar = observer((props: QnaMakerSynonymGroupRightCommandBarProps) => {
    const { onSave, onEdit, onDiscard, isInDirtyState, isInEmptyState, isEditSynonymOn, areEditActionsDisabled } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const saveChangesButtonIdRef = React.useRef<string>(getId());
    const discardChangesButtonIdRef = React.useRef<string>(getId());
    const editButtonIdRef = React.useRef<string>(getId());

    const [isEditButtonDisabled, setIsEditButtonDisabled] = React.useState(true);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = React.useState(true);
    const [isDiscardButtonDisabled, setIsDiscardButtonDisabled] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [isDiscardModalHidden, setIsDiscardModalHidden] = React.useState(true);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('SynonymEdit');
            dequeueTeachingBubble('SynonymSaveChanges');
        },
        []
    );

    React.useEffect(() => {
        if (isInDirtyState && !isSaveButtonDisabled && !uiStore.teachingBubbles.SynonymSaveChanges) {
            createTeachingBubble(
                'SynonymSaveChanges',
                localize(qnaMakerTeachingBubbleMessages.synonymSaveChangesBody),
                localize(qnaMakerTeachingBubbleMessages.saveChangesTitle),
                `#${saveChangesButtonIdRef.current}`,
                true
            );

            return;
        }

        if (!isEditButtonDisabled && !uiStore.teachingBubbles.SynonymEdit) {
            createTeachingBubble(
                'SynonymEdit',
                localize(qnaMakerTeachingBubbleMessages.synonymEditBody),
                localize(qnaMakerTeachingBubbleMessages.synonymEditTitle),
                `#${editButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            isInDirtyState &&
            !isDiscardButtonDisabled &&
            !uiStore.teachingBubbles.SynonymSaveChanges &&
            !uiStore.teachingBubbles.SynonymDiscardChanges
        ) {
            createTeachingBubble(
                'SynonymDiscardChanges',
                localize(qnaMakerTeachingBubbleMessages.synonymDiscardChangesBody),
                localize(qnaMakerTeachingBubbleMessages.discardChangesTitle),
                `#${discardChangesButtonIdRef.current}`,
                true
            );
        }
    }, [isInDirtyState, isSaveButtonDisabled, isEditButtonDisabled, isDiscardButtonDisabled, Object.values(uiStore.teachingBubbles)]);

    const onEditClick = () => {
        logQnaKBButtonClick(serviceName, 'SynonymsCommandbar', 'Button', 'EditSynonym');
        onEdit();
    };

    const onSaveClick = () => {
        logQnaKBButtonClick(serviceName, 'SynonymsCommandbar', 'Button', 'SaveSynonym');
        onSave();
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isEditSynonymOn || areEditActionsDisabled || isInEmptyState;
                setIsEditButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onEditClick}
                                id={editButtonIdRef.current}
                                name={localize(messages.edit)}
                                text={localize(messages.edit)}
                                iconProps={{ iconName: 'Edit' }}
                                disabled={!canWrite || _disabled}
                                ariaLabel={localize(messages.edit)}
                                data-automation-id="editSynonymsButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'saveChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isEditSynonymOn || areEditActionsDisabled;
                setIsSaveButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onSaveClick}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                id={saveChangesButtonIdRef.current}
                                name={localize(messages.saveChanges)}
                                text={localize(messages.saveChanges)}
                                data-automation-id="saveChangesButton"
                                ariaLabel={localize(messages.saveChanges)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'discardChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isEditSynonymOn || areEditActionsDisabled;
                setIsDiscardButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="discardChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={() => setIsDiscardModalHidden(false)}
                                iconProps={{ iconName: 'ChromeClose' }}
                                disabled={!canWrite || _disabled}
                                id={discardChangesButtonIdRef.current}
                                name={localize(messages.discardChanges)}
                                text={localize(messages.discardChanges)}
                                data-automation-id="discardChangesButton"
                                ariaLabel={localize(messages.discardChanges)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        }
    ];

    const handleDiscard = () => {
        onDiscard();
        setIsDiscardModalHidden(true);
    };

    return (
        <>
            <LuisCommandBar hideBorder items={items} />
            <QnaMakerDiscardModal onSubmit={handleDiscard} hidden={isDiscardModalHidden} onDismiss={() => setIsDiscardModalHidden(true)} />
        </>
    );
});
